import { ref, watch } from 'vue';
import { defineStore } from 'pinia';
import {
  getLocalStorageItem,
  setLocalStorageItem,
  SETTINGS_SAVED_SEARCHES,
} from '@/stores/localStorage';

export interface SavedSearch {
  id: string;
  name: string;
  tags: string[];
  alias?: string;
}

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

export const useSavedSearchesStore = defineStore('savedSearches', () => {
  const searches = ref<SavedSearch[]>(
    getLocalStorageItem(SETTINGS_SAVED_SEARCHES, []),
  );
  watch(searches, (v) => setLocalStorageItem(SETTINGS_SAVED_SEARCHES, v), { deep: true });

  // Which saved searches are currently toggled ON (tags ANDed into search)
  const activeAliasIds = ref<Set<string>>(new Set());

  function add(s: Omit<SavedSearch, 'id'>) {
    searches.value.push({
 ...s,
id: generateId(),
});
  }

  function remove(id: string) {
    searches.value = searches.value.filter((s) => s.id !== id);
    activeAliasIds.value.delete(id);
  }

  function update(id: string, patch: Partial<Omit<SavedSearch, 'id'>>) {
    const idx = searches.value.findIndex((s) => s.id === id);
    if (idx !== -1) {
      searches.value[idx] = {
 ...searches.value[idx],
...patch,
};
    }
  }

  function toggleAlias(id: string) {
    if (activeAliasIds.value.has(id)) {
      activeAliasIds.value.delete(id);
    } else {
      activeAliasIds.value.add(id);
    }
    // Trigger reactivity — replace the Set
    activeAliasIds.value = new Set(activeAliasIds.value);
  }

  function isActive(id: string): boolean {
    return activeAliasIds.value.has(id);
  }

  function storeCurrentSearch(name: string, tags: string[], alias?: string) {
    add({
 name,
tags,
alias,
});
  }

  /** Tags from all currently active alias saved searches */
  function activeAliasTags(): string[] {
    return searches.value
      .filter((s) => activeAliasIds.value.has(s.id))
      .flatMap((s) => s.tags);
  }

  return {
    searches,
    activeAliasIds,
    add,
    remove,
    update,
    toggleAlias,
    isActive,
    storeCurrentSearch,
    activeAliasTags,
  };
});
